import React, { useEffect } from 'react';
import Layout from "../temp/layout/Layout";
import { Helmet } from 'react-helmet';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Collapse } from 'antd';
import { FaQuestionCircle, FaSearch } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';

const { Panel } = Collapse;

const FAQ = () => {
    useEffect(() => {
        AOS.init({ 
            duration: 1000, 
            once: true,
            offset: 100
        });
    }, []);

    const faqs = [
        {
            q: "Is Educational Eternity cloud-based and secure?",
            a: "Yes, our platform is 100% cloud-based with high-grade security. All data is encrypted, backed up regularly, and compliant with international data protection standards. Your institution's data is safe and accessible from anywhere, anytime."
        },
        {
            q: "Can parents track fee payments and academic progress?",
            a: "Absolutely! Parents have their own dedicated portal where they can view fee statements, make online payments, download receipts, track their child's attendance, grades, assignments, and receive real-time notifications about important updates."
        },
        {
            q: "Is data migration from existing systems supported?",
            a: "Yes, our expert onboarding team provides complete data migration support. We help you seamlessly transfer all your existing student records, teacher data, academic history, and administrative information to Educational Eternity without any data loss."
        },
        {
            q: "How does the AI Mentorship feature work?",
            a: "Our AI Mentorship system analyzes student performance, learning patterns, and academic history to provide personalized study recommendations, identify learning gaps, suggest improvement areas, and create customized learning paths for each student."
        },
        {
            q: "Can we customize the system according to our institution's needs?",
            a: "Yes, Educational Eternity is highly customizable. We offer configuration support to tailor the system according to your institution's specific requirements, workflows, and academic structure."
        },
        {
            q: "What devices are supported?",
            a: "Educational Eternity works seamlessly on all devices - desktop computers, laptops, tablets, and smartphones. We have dedicated mobile apps for Android and iOS, plus a fully responsive web application."
        },
        {
            q: "Is training provided for staff and administrators?",
            a: "Yes, we provide comprehensive training sessions for all users - administrators, teachers, and staff. Our team ensures everyone is comfortable using the system before going live."
        },
        {
            q: "What kind of support do you offer?",
            a: "We offer 24/7 technical support via email, phone, and live chat. Our support team is always ready to help with any questions or issues. We also provide regular updates and feature enhancements."
        },
        {
            q: "How does attendance management work?",
            a: "Our attendance system supports multiple methods including biometric integration, manual entry, and mobile app marking. It automatically sends SMS alerts to parents, generates reports, and tracks attendance patterns."
        },
        {
            q: "Can we conduct online exams through the platform?",
            a: "Yes, our comprehensive online exam system includes question banks, secure exam delivery, real-time monitoring, anti-cheating measures, auto-grading, and instant result generation."
        },
        {
            q: "How is fee management handled?",
            a: "The fee management system supports multiple payment gateways, automated fee reminders, payment tracking, receipt generation, and comprehensive financial reports. Parents can pay fees online through the portal."
        },
        {
            q: "What is the implementation timeline?",
            a: "Implementation typically takes 2-4 weeks depending on your institution's size and requirements. This includes data migration, system configuration, user training, and go-live support."
        },
        {
            q: "Is there a free trial available?",
            a: "Yes, we offer a free 1-year trial plan that includes all features. This allows you to experience the full platform before making a commitment."
        },
        {
            q: "Can multiple campuses be managed from one system?",
            a: "Yes, our Enterprise Suite supports multi-campus management, allowing you to manage multiple branches, campuses, or locations from a single centralized system."
        },
        {
            q: "How secure is student and institutional data?",
            a: "We use enterprise-level security with data encryption, regular backups, secure cloud hosting, and compliance with international data protection standards. Your data is protected with the highest security measures."
        }
    ];

    return (
        <Layout>
            <Helmet>
                <title>FAQ - Educational Eternity | Frequently Asked Questions</title>
                <meta name="description" content="Find answers to frequently asked questions about Educational Eternity - features, pricing, implementation, support, and more." />
            </Helmet>

            {/* Hero Section */}
            <section className="relative w-full h-[50vh] bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: `url(assets/images/home1.jpg)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}></div>
                <div className="relative z-10 text-center px-4">
                    <div className="mb-6" data-aos="fade-up">
                        <FaQuestionCircle className="text-6xl md:text-8xl text-white/80 mx-auto mb-4" />
                    </div>
                    <h1 data-aos="fade-up" data-aos-delay="100" className="text-5xl md:text-7xl font-bold text-white mb-4">
                        Frequently Asked Questions
                    </h1>
                    <p data-aos="fade-up" data-aos-delay="200" className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
                        Everything you need to know about Educational Eternity
                    </p>
                </div>
            </section>

            {/* Search Bar */}
            <section className="py-8 bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm">
                <div className="container mx-auto px-4 max-w-3xl">
                    <div className="relative" data-aos="fade-up">
                        <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
                        <input
                            type="text"
                            placeholder="Search for questions..."
                            className="w-full pl-12 pr-4 py-4 rounded-full border-2 border-gray-200 focus:border-blue-500 focus:outline-none text-lg"
                        />
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4 max-w-4xl">
                    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12" data-aos="fade-up">
                        <Collapse 
                            accordion 
                            size="large"
                            items={faqs.map((faq, i) => ({
                                key: i,
                                label: <span className="text-lg font-semibold text-gray-900">{faq.q}</span>,
                                children: <p className="text-gray-700 leading-relaxed text-base">{faq.a}</p>
                            }))}
                            className="faq-collapse"
                        />
                    </div>
                </div>
            </section>

            {/* Still Have Questions Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 text-center" data-aos="fade-up">
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                        Still Have Questions?
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                        Can't find the answer you're looking for? Our support team is here to help.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/contact">
                            <button className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full text-lg font-bold shadow-2xl hover:shadow-blue-500/50 transform hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto">
                                Contact Support <FaArrowRight />
                            </button>
                        </Link>
                        <Link to="/plans">
                            <button className="px-8 py-4 bg-white border-2 border-blue-600 text-blue-600 rounded-full text-lg font-bold hover:bg-blue-50 transform hover:scale-105 transition-all duration-300">
                                View Plans
                            </button>
                        </Link>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default FAQ;

